// Abstand zwischen zwei Punkten
export function distance(p1, p2) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

// Fläche eines Polygons (Shoelace-Formel)
export function polygonArea(points) {
  if (points.length < 3) return 0;

  let area = 0;
  const n = points.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }

  return Math.abs(area) / 2;
}

// Schwerpunkt eines Polygons
export function polygonCentroid(points) {
  if (points.length === 0) return { x: 0, y: 0 };

  let sumX = 0;
  let sumY = 0;

  for (const p of points) {
    sumX += p.x;
    sumY += p.y;
  }

  return {
    x: sumX / points.length,
    y: sumY / points.length,
  };
}

// Mittelpunkt einer Linie
export function midpoint(p1, p2) {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
}

// Winkel einer Linie (in Radiant)
export function lineAngle(p1, p2) {
  return Math.atan2(p2.y - p1.y, p2.x - p1.x);
}

// Berechnet die Außenpunkte einer Wand aus den Innenpunkten
// innerStart, innerEnd: Die Innenkanten-Punkte
// thickness: Wandstärke in mm
// flipped: Wenn true, Außenseite ist rechts statt links
// Gibt { outerStart, outerEnd } zurück
export function calculateOuterPoints(innerStart, innerEnd, thickness, flipped = false) {
  const angle = lineAngle(innerStart, innerEnd);
  // Normal-Winkel: Links = +90°, Rechts = -90°
  const normalAngle = flipped ? (angle - Math.PI / 2) : (angle + Math.PI / 2);
  const nx = Math.cos(normalAngle) * thickness;
  const ny = Math.sin(normalAngle) * thickness;

  return {
    outerStart: {
      x: innerStart.x + nx,
      y: innerStart.y + ny,
      z: innerStart.z,
    },
    outerEnd: {
      x: innerEnd.x + nx,
      y: innerEnd.y + ny,
      z: innerEnd.z,
    },
  };
}

// Punkt auf Linie projizieren
export function projectPointOnLine(point, lineStart, lineEnd) {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) return { ...lineStart };

  const t = Math.max(0, Math.min(1,
    ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSq
  ));

  return {
    x: lineStart.x + t * dx,
    y: lineStart.y + t * dy,
    t, // Parameter 0-1
  };
}

// Abstand Punkt zu Linie
export function pointToLineDistance(point, lineStart, lineEnd) {
  const projected = projectPointOnLine(point, lineStart, lineEnd);
  return distance(point, projected);
}

// Snap zu Grid
export function snapToGrid(value, gridSize) {
  return Math.round(value / gridSize) * gridSize;
}

// ID generieren
export function generateId() {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Formatierung: mm zu m mit 2 Dezimalstellen
export function formatLength(mm) {
  return (mm / 1000).toFixed(2);
}

// Formatierung: mm² zu m²
export function formatArea(mmSq) {
  return (mmSq / 1000000).toFixed(2);
}

// Prüfen ob ein Punkt auf einer Linie liegt (mit Toleranz)
export function isPointOnLine(point, lineStart, lineEnd, tolerance = 50) {
  const dist = pointToLineDistance(point, lineStart, lineEnd);
  return dist <= tolerance;
}

// Überlappende Länge einer Kante auf einer Wand berechnen
// Gibt die Länge zurück, die eine Polygon-Kante mit einer Wand teilt
export function getEdgeOverlapOnWall(edgeStart, edgeEnd, wallStart, wallEnd, tolerance = 50) {
  // Prüfen ob beide Kanten-Endpunkte auf der Wandlinie liegen
  const startOnWall = isPointOnLine(edgeStart, wallStart, wallEnd, tolerance);
  const endOnWall = isPointOnLine(edgeEnd, wallStart, wallEnd, tolerance);

  if (!startOnWall || !endOnWall) {
    return 0; // Kante liegt nicht auf der Wand
  }

  // Projiziere beide Punkte auf die Wandlinie
  const projStart = projectPointOnLine(edgeStart, wallStart, wallEnd);
  const projEnd = projectPointOnLine(edgeEnd, wallStart, wallEnd);

  // Prüfen ob die Projektion innerhalb der Wand liegt (t zwischen 0 und 1)
  const t1 = Math.max(0, Math.min(1, projStart.t));
  const t2 = Math.max(0, Math.min(1, projEnd.t));

  // Wenn beide außerhalb oder beide am selben Punkt, keine Überlappung
  if (t1 === t2) return 0;

  // Länge der Überlappung berechnen
  const wallLength = distance(wallStart, wallEnd);
  const overlapLength = Math.abs(t2 - t1) * wallLength;

  return overlapLength;
}

// ============================================
// BOGEN / ARC FUNKTIONEN
// ============================================

/**
 * Berechnet einen Kreismittelpunkt durch 3 Punkte
 * Verwendet die Umkreisformel für ein Dreieck
 * @returns {Object|null} { cx, cy, radius } oder null wenn Punkte kollinear
 */
export function circleFrom3Points(p1, p2, p3) {
  const ax = p1.x, ay = p1.y;
  const bx = p2.x, by = p2.y;
  const cx = p3.x, cy = p3.y;

  // Determinante prüfen (kollineare Punkte = kein Kreis möglich)
  const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
  if (Math.abs(d) < 0.0001) {
    return null; // Punkte sind kollinear
  }

  // Kreismittelpunkt berechnen
  const ax2ay2 = ax * ax + ay * ay;
  const bx2by2 = bx * bx + by * by;
  const cx2cy2 = cx * cx + cy * cy;

  const centerX = (ax2ay2 * (by - cy) + bx2by2 * (cy - ay) + cx2cy2 * (ay - by)) / d;
  const centerY = (ax2ay2 * (cx - bx) + bx2by2 * (ax - cx) + cx2cy2 * (bx - ax)) / d;

  const radius = Math.sqrt((ax - centerX) ** 2 + (ay - centerY) ** 2);

  return { cx: centerX, cy: centerY, radius };
}

/**
 * Generiert Punkte entlang eines Bogens (3-Punkt-Bogen)
 * @param {Object} start - Startpunkt {x, y}
 * @param {Object} through - Punkt auf dem Bogen {x, y}
 * @param {Object} end - Endpunkt {x, y}
 * @param {number} segments - Anzahl der Segmente (mehr = glatter)
 * @returns {Array} Array von Punkten entlang des Bogens inkl. Start und Ende
 */
export function generateArcPoints(start, through, end, segments = 8) {
  // Issue #11: Vollkreis wenn Start = Ende
  const isFullCircle = Math.abs(start.x - end.x) < 1 && Math.abs(start.y - end.y) < 1;

  if (isFullCircle) {
    // Vollkreis: Berechne Mittelpunkt aus Start und Through
    // Der Through-Punkt liegt auf dem Kreis, Start ist auch auf dem Kreis
    // Kreismittelpunkt ist senkrecht zur Linie Start-Through, gleicher Abstand
    const midX = (start.x + through.x) / 2;
    const midY = (start.y + through.y) / 2;
    const halfDist = distance(start, through) / 2;

    // Senkrechte Richtung
    const dx = through.x - start.x;
    const dy = through.y - start.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 0.001) return [start]; // Punkte identisch

    // Normalisierte Senkrechte
    const nx = -dy / len;
    const ny = dx / len;

    // Radius = Abstand von Start zum Mittelpunkt
    // Mittelpunkt liegt auf der Senkrechten durch die Mitte von Start-Through
    // Abstand vom Mittelpunkt zum Start muss = Abstand zum Through sein
    // Das bedeutet Mittelpunkt ist auf der Mittelsenkrechten

    // Für einen Vollkreis mit Through auf der gegenüberliegenden Seite:
    // Radius = halfDist (Through ist Durchmesserpunkt)
    const radius = halfDist;
    const cx = midX;
    const cy = midY;

    // Vollkreis generieren (mehr Segmente für glatt)
    const fullCircleSegments = Math.max(segments, 24);
    const points = [];
    for (let i = 0; i <= fullCircleSegments; i++) {
      const angle = (i / fullCircleSegments) * 2 * Math.PI;
      points.push({
        x: cx + radius * Math.cos(angle),
        y: cy + radius * Math.sin(angle),
      });
    }
    return points;
  }

  const circle = circleFrom3Points(start, through, end);

  if (!circle) {
    // Punkte sind kollinear - gib einfach Start und Ende zurück
    return [start, end];
  }

  const { cx, cy, radius } = circle;

  // Winkel für alle 3 Punkte berechnen
  const startAngle = Math.atan2(start.y - cy, start.x - cx);
  const throughAngle = Math.atan2(through.y - cy, through.x - cx);
  const endAngle = Math.atan2(end.y - cy, end.x - cx);

  // Richtung bestimmen (im Uhrzeigersinn oder gegen)
  // Der through-Punkt bestimmt die Richtung
  let totalAngle = endAngle - startAngle;

  // Normalisiere auf -PI bis PI
  while (totalAngle > Math.PI) totalAngle -= 2 * Math.PI;
  while (totalAngle < -Math.PI) totalAngle += 2 * Math.PI;

  // Prüfen ob through-Punkt im korrekten Bogen liegt
  let throughRelative = throughAngle - startAngle;
  while (throughRelative > Math.PI) throughRelative -= 2 * Math.PI;
  while (throughRelative < -Math.PI) throughRelative += 2 * Math.PI;

  // Wenn through nicht im direkten Bogen liegt, nehme den längeren Weg
  if ((totalAngle > 0 && throughRelative < 0) || (totalAngle < 0 && throughRelative > 0)) {
    // Nehme den längeren Weg (Komplement)
    if (totalAngle > 0) {
      totalAngle = totalAngle - 2 * Math.PI;
    } else {
      totalAngle = totalAngle + 2 * Math.PI;
    }
  }

  // Punkte generieren
  const points = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const angle = startAngle + totalAngle * t;
    points.push({
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    });
  }

  return points;
}

/**
 * Berechnet die Bogenlänge zwischen zwei Winkeln
 */
export function arcLength(radius, startAngle, endAngle) {
  let angle = Math.abs(endAngle - startAngle);
  return radius * angle;
}
